import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { gradeById } from '../data/grades';
import issues, { latestIssueByGrade } from '../data/newsletters';


const Lightbox = ({ src, alt, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    role="dialog"
    aria-modal="true"
    aria-label={alt}
    onClick={onClose}
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2 focus:outline-none focus:ring-2 focus:ring-white"
      aria-label="Close image"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
    <img
      src={src}
      alt={alt}
      className="max-w-full max-h-[90vh] rounded-xl shadow-2xl object-contain"
      onClick={(e) => e.stopPropagation()}
    />
  </div>
);

const Newsletters = () => {
  const [lightbox, setLightbox] = useState(null);

  // Deep links like /newsletters#grade-2 (from /our-classes). The prerendered
  // HTML handles this on its own; this covers the client-rendered dev server.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    // 'instant': the site sets scroll-behavior: smooth, which would otherwise
    // animate the whole page from the top on load.
    if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen font-sans text-gray-800 selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* Hero */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" style={{ backgroundColor: '#0F2240' }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-school-gold/12 rounded-full blur-3xl" />
            <div className="absolute -left-10 bottom-0 w-60 h-60 bg-school-gold/6 rounded-full blur-2xl" />
          </div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-sm rounded-full mb-8 border border-white/20">
              <svg className="w-8 h-8 text-school-gold" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5z"/>
              </svg>
            </div>
            <span className="text-school-gold font-bold tracking-[0.2em] uppercase text-xs mb-4 block">
              Stay Connected
            </span>
            <h1
              className="font-heading font-bold text-white tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              Grade Team{' '}
              <em className="text-school-gold font-light not-italic">Newsletters</em>
            </h1>
            <p
              className="text-white/55 font-light leading-relaxed max-w-xl mx-auto"
              style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}
            >
              Monthly newsletters from each grade team keeping families informed and connected.
            </p>
          </div>
        </section>

        {/* Newsletters, one section per monthly issue */}
        {issues.map((issue, issueIndex) => (
        <section
          key={issue.id}
          aria-labelledby={`${issue.id}-heading`}
          className={`py-16 px-4 sm:px-6 lg:px-8 ${issueIndex === 0 ? '' : 'border-t border-school-blue/10'}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className="mb-12 flex flex-wrap items-baseline gap-x-4 gap-y-2">
              <h2
                id={`${issue.id}-heading`}
                className="font-heading font-bold text-school-blue tracking-tight"
                style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)' }}
              >
                {issue.month}
              </h2>
              {issue.note && (
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-school-goldDark">
                  Archive · {issue.note}
                </span>
              )}
            </div>

            <div className="space-y-16">
            {issue.entries.map((entry) => {
              const g = gradeById[entry.grade];
              return (
              <div
                key={entry.grade}
                // Only a grade's newest issue takes the id (ids must be unique per page).
                id={latestIssueByGrade[entry.grade] === issue ? g.id : undefined}
                className="scroll-mt-28"
              >
                {/* Grade header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: g.color }} aria-hidden="true">
                    <span className="text-white font-bold text-sm">{g.short}</span>
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-school-blue">{g.label}</h3>
                  <div className="flex-1 h-px bg-gray-200 ml-2" aria-hidden="true" />
                </div>

                {/* Files grid */}
                <div className={`grid gap-6 ${entry.files.length === 1 ? 'grid-cols-1 max-w-sm' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                  {entry.files.map((file) =>
                    file.type === 'pdf' ? (
                      <a
                        key={file.src}
                        href={file.src}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-school-blue/40 bg-white flex flex-col"
                        aria-label={`Open ${g.label} ${file.name} PDF (opens in new window)`}
                      >
                        {/* First-page preview. Not an <iframe>: the site's X-Frame-Options:
                            DENY blocks framing the PDF, mobile browsers don't render PDFs
                            in frames, and a frame downloads the whole file up front. */}
                        <div className="aspect-[3/4] overflow-hidden bg-gray-50 relative">
                          <img
                            src={file.thumb}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                          />
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                        </div>
                        {/* Footer */}
                        <div className="p-4 flex items-center justify-between">
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{file.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">Click to open PDF</p>
                          </div>
                          <span
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white shrink-0 transition-opacity group-hover:opacity-80"
                            style={{ backgroundColor: g.color }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                            </svg>
                            Open
                          </span>
                        </div>
                      </a>
                    ) : (
                      <button
                        key={file.src}
                        onClick={() => setLightbox({ src: file.src, alt: `${g.label} ${file.name}` })}
                        className="group relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-school-blue/40 bg-white text-left"
                        aria-label={`View ${file.name} for ${g.label}`}
                      >
                        <div className="aspect-[3/4] overflow-hidden bg-gray-50 relative">
                          <img
                            src={file.src}
                            alt={`${g.label} ${file.name}`}
                            loading="lazy"
                            className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                          />
                          {/* Overlay hint — scoped to the image only so it never
                              sits over the footer text below (was inset-0 on the
                              whole card, which made the text's contrast undecidable) */}
                          <div className="absolute inset-0 bg-school-blue/0 group-hover:bg-school-blue/5 transition-colors" />
                        </div>
                        <div className="p-4 flex items-center justify-between">
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{file.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">Click to enlarge</p>
                          </div>
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: g.lightColor, color: g.color }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                            </svg>
                          </div>
                        </div>
                      </button>
                    )
                  )}
                </div>
              </div>
              );
            })}
            </div>
          </div>
        </section>
        ))}

      </main>

      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

      <Footer />
    </div>
  );
};

export default Newsletters;
